import React, { useEffect, useRef } from 'react';
import { BadgeProps, BadgeSize, BadgeVariant } from '../../../types/DesignSystem/Badge';
import { formatCount } from '../formatCount';
import { withClassName } from '../cn';

/**
 * § 300 — the count TICKS. A Badge counts things, and a count that changes in place
 * should be seen to change: the new figure rises in from below when the number went up
 * and drops in from above when it went down (`count-up` / `count-down`, 200ms on the
 * settle curve, entrance-only). The `key` on the figure remounts it on every change so
 * the keyframe replays even when two changes run the same way; `tabular-nums` (§ 219)
 * holds the pill's width while it does. Only a NUMERIC child ticks — a text badge is
 * untouched, byte for byte — and the first mount never ticks: a badge appearing is not
 * a count changing. `data-tick` carries the direction for the frame it applies.
 */
const isNumeric = (value: React.ReactNode): value is number | string =>
  typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value));

const useTickDirection = (children: React.ReactNode): 'up' | 'down' | null => {
  const previous = useRef<React.ReactNode>(children);
  /* Read the previous value DURING this render (the effect below moves it after
     paint), so the render in which the count changed is the one that animates. */
  const direction =
    isNumeric(children) && isNumeric(previous.current) && previous.current !== children
      ? (Number(children) > Number(previous.current) ? 'up' : 'down')
      : null;
  useEffect(() => {
    previous.current = children;
  }, [children]);
  return direction;
};

/**
 * **Badge counts things; `Status` states things** — ADS's Badge/Lozenge split,
 * adopted 2026-08-25 (§ 51). Every live caller already uses Badge this way
 * (Button's badge slot, review-pending counts, attribute-change counts); a
 * textual state chip belongs on `Status`, which owns the six tones.
 *
 * `warning` therefore wears its own family's ink, `yellow-warning-950` — the
 * same ink `Status` sets warning in, so the two components cannot disagree
 * about what "warning" looks like. It wore the brand navy before: legal by
 * contrast, wrong by grammar. (And 950, not 900: 900 is 4.32:1 on the tint —
 * the statusChip lesson.)
 *
 * `primary` looks broken on a white page — white pill, invisible border — and
 * is not: it is the count bubble ON A FILLED BUTTON, where the fill provides
 * its ground. The invisible borders on the other variants are size-parity with
 * each other, so the variants line up pixel-equal in a row.
 *
 * `neutral` (§ 208) is the QUIET count — sunken ground, hairline edge,
 * secondary ink (6.57:1 on the sunken ground in light, 7.08:1 in dark — the
 * same measured pair `Tag meta` wears, § 66) — for a
 * number that informs rather than demands: a per-section total in a filter
 * menu, a result count. It is, verbatim, the pill `ApplicationsMenu` had to
 * hand-token in the Performance migration because Badge had no variant that
 * could whisper; that TODO retires when its call site next gets touched.
 *
 * ## § 241 — the pill is INLINE, and it is a span (2026-09-09)
 *
 * The root was a `<div>` with `tw-flex`: a BLOCK-level box, so anywhere the badge
 * sat outside a flex row it stretched to its container's full width — measured on
 * the live showcase, every standalone badge rendered as a ~900px pill. (§ 111's
 * "badge passed as a Button child" incident was this same fact seen from inside a
 * button.) `tw-inline-flex` sizes the pill to its count in every context, and the
 * `<span>` makes it legal phrasing content inside the `<button>` it most often
 * lives in — a `<div>` there was invalid HTML.
 *
 * The `warning` ring was `tw-border-yellow-warning-500` — the last raw palette-step
 * class in the design system, and a literal that could never theme: #ffc400 on the
 * dark tint. The ring is now the tint itself (invisible, size parity with the other
 * variants); the family reads through ground + ink, exactly as `Status`/`Tag`
 * warning chips do.
 */
const variantStyles: Record<BadgeVariant, string> = {
  warning: 'tw-bg-tone-warning-tint tw-border tw-border-tone-warning-tint tw-text-tone-warning-ink',
  primary: 'tw-bg-surface tw-border tw-border-surface tw-text-accent',
  secondary: 'tw-bg-accent-bold tw-border tw-border-accent-bold tw-text-ink-inverse',
  disabled: 'tw-bg-surface-sunken tw-border tw-border-surface-sunken tw-text-ink-disabled',
  neutral: 'tw-bg-surface-sunken tw-border tw-border-edge tw-text-ink-secondary',
  /* § 295 — the count on a selected thing: the § 51 selection pair, `Tag selected`'s
     exact classes. The ring is the tint itself — size parity with the others. */
  selected: 'tw-bg-accent-tint tw-border tw-border-accent-tint tw-text-accent',
};

/*
 * § 295 — the two rungs. `md` is byte-identical to what every Badge rendered before
 * (28px, the Tag-matching chip height). `sm` is the COUNT: 20px like `Status`, a
 * tighter inset, and `min-w-5` so a single digit is a circle rather than an oval.
 * The type token does not change with the size — size is density (`controlSize.ts`).
 */
const sizeStyles: Record<BadgeSize, string> = {
  md: 'tw-min-h-7 tw-px-2 tw-py-0.5 tw-gap-2',
  sm: 'tw-min-h-5 tw-min-w-5 tw-px-1.5 tw-gap-1',
};

/* The icon box scales with the rung, so a 20px pill is not asked to hold a 20px glyph. */
const iconBox: Record<BadgeSize, string> = {
  md: 'tw-w-5 tw-h-5',
  sm: 'tw-w-4 tw-h-4',
};

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge({
  variant = 'secondary',
  size = 'md',
  max,
  children,
  className = '',
  icon,
  showIcon,
  'data-testid': dataTestId,
  title,
  ...rest
}, ref) {
  // Default showIcon to true when icon is provided
  const shouldShowIcon = showIcon ?? !!icon;
  const tick = useTickDirection(children);

  /*
    § 303 — what the figure PRINTS. A number child is formatted with the locale's
    thousands separator (`formatCount`); a digit STRING prints exactly as given, so a
    caller's own "007" is never rewritten. `max` caps either: over it the pill shows
    "99+" and the full figure moves to `title` and an sr-only twin, so a screen reader
    hears the count and a hover shows it. The tick keys on the RAW child — a cap does
    not change which way the count moved.
  */
  const count = isNumeric(children) ? Number(children) : null;
  const capped = count !== null && max !== undefined && count > max;
  const printed = count === null
    ? null
    : capped
      ? `${formatCount(max as number)}+`
      : typeof children === 'number' ? formatCount(children) : String(children);
  const full = count !== null ? formatCount(count) : undefined;

  return (
    <span
      /* rest first — the Button.tsx rule (§ 241 brought Badge onto it). */
      {...rest}
      ref={ref}
      /* `tabular-nums` (§ 219): a Badge counts things, and a count that ticks
         9→10→11 in proportional figures wobbles its pill width on every change.
         Pagination and Progress already set it; the third numeral chip joins.
         `justify-center` (§ 295): with `sm`'s `min-w-5` a lone digit has room to sit
         off-centre; content-sized `md` is unaffected. */
      className={withClassName(`tw-inline-flex tw-items-center tw-justify-center ${sizeStyles[size]} tw-rounded-full tw-text-sub-title-label tw-tabular-nums tw-border-solid tw-shrink-0 ${variantStyles[variant]}`, className)}
      data-testid={dataTestId}
      data-size={size}
      data-tick={tick ?? undefined}
      data-capped={capped ? 'true' : undefined}
      /* A caller's own title wins; a capped count supplies the full figure otherwise. */
      title={title ?? (capped ? full : undefined)}
    >
      {count !== null ? (
        /* § 300 — the figure in its own box so it can move; keyed on the value so a
           change remounts it and the entrance replays. `inline-block` gives the
           translate something to move. § 303 — a CAPPED figure is decorative: the
           sr-only twin beside it carries the real count. */
        <>
          <span
            key={String(children)}
            aria-hidden={capped || undefined}
            className={`tw-inline-block ${tick === 'up' ? 'motion-safe:tw-animate-count-up' : tick === 'down' ? 'motion-safe:tw-animate-count-down' : ''}`.trim()}
          >
            {printed}
          </span>
          {capped && <span className="tw-sr-only">{full}</span>}
        </>
      ) : (
        children
      )}
      {shouldShowIcon && icon && (
        <span className={`tw-flex tw-items-center tw-justify-center tw-shrink-0 ${iconBox[size]}`}>
          {icon}
        </span>
      )}
    </span>
  );
});

export default Badge;
